/**
 * THE LINK FROM AN INVITATION OR A RESET, REDEEMED ONLY WHEN THE PERSON PRESSES CONTINUE.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * WHY OPENING THE LINK NO LONGER SIGNS YOU IN
 * ─────────────────────────────────────────────────────────────────────────
 *
 * The mails used to link to GoTrue's own `/verify?token=…`, and a plain GET of that address
 * redeems the one-time credential. Mail security gateways open every link in an incoming
 * message before its reader does — Microsoft 365's Safe Links, Trend Micro's time-of-click
 * scan — so the gateway spent the credential and the reader's own click found "expired".
 * Measured on prod, 2026-10-14, for an address behind Trend Micro and Microsoft 365: the
 * invitation and two resets were each redeemed 8 to 12 seconds after they were sent, three
 * times out of three, and the person never got in. The six-digit code could not help: it is
 * the same credential and it died with the link.
 *
 * So the mails now carry GoTrue's HASHED token in this page's fragment
 * (`auth-fragment.ts`, `AuthLinkConfirm`), and nothing happens on load. A scanner fetches
 * the page and never presses the button; the person presses it, and `verifyOtp` exchanges
 * the token for a session then. The page must stay that way: any code that redeems the
 * token without a press — on mount, on focus, on a timer — brings the defect back.
 */
import { useState, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { humanError } from '@/lib/human-error';
import type { CodePurpose } from './CodeScreen';

const COPY: Record<CodePurpose, { h1: string; sub: string }> = {
  invite: { h1: 'You have been invited.', sub: 'Continue to choose your password.' },
  recovery: { h1: 'Reset your password.', sub: 'Continue to choose a new password.' },
  magiclink: { h1: 'Sign in.', sub: 'Continue to sign in.' },
};

export function ConfirmScreen({
  purpose,
  tokenHash,
  onAttempt,
  onRedeemed,
  onRejected,
  onSpent,
  onBack,
}: {
  purpose: CodePurpose;
  tokenHash: string;
  /**
   * Fired BEFORE the exchange, for the reason `CodeScreen`'s prop of the same name gives:
   * `verifyOtp` notifies the auth subscribers before its own promise resolves, so the gate
   * must already know this session came from a link or an invited user flashes past the
   * set-password screen.
   */
  onAttempt: () => void;
  /**
   * The session has landed. The gate holds this frame up while a link is unsettled, so unlike
   * `CodeScreen` this step has to SAY it is done — waiting for the gate to swap on its own left
   * it on "Opening…" forever, with the person signed in behind it.
   */
  onRedeemed: () => void;
  /** Nothing was signed in, so the intent is given back. */
  onRejected: () => void;
  /** GoTrue refused the token itself — expired or already used. Nothing to retry here. */
  onSpent: () => void;
  onBack: () => void;
}): ReactNode {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const copy = COPY[purpose];

  async function redeem(): Promise<void> {
    if (busy) return;
    setBusy(true);
    setError(null);
    onAttempt();

    const { error: err } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: purpose });

    if (err) {
      onRejected();
      /* 403 `otp_expired` is GoTrue saying the token is gone: aged out, used, or replaced by a
         newer mail. Anything else — the network, a rate limit — may work on a second press. */
      if (err.status === 403 || err.code === 'otp_expired') {
        onSpent();
        return;
      }
      setError(humanError(err, 'That did not go through. Try again.'));
      setBusy(false);
      return;
    }
    /* Busy until the gate swaps the tree, which `onRedeemed` lets it do. */
    onRedeemed();
  }

  return (
    <section className="lm-si-step">
      <h1 className="lm-si-step-h1">{copy.h1}</h1>
      <p className="lm-si-step-sub">{copy.sub}</p>

      <div className="lm-si-step-actions">
        {error && (
          <p role="alert" className="lm-si-alert">
            {error}
          </p>
        )}
        <button
          type="button"
          className="lm-si-primary lm-si-amber"
          disabled={busy}
          onClick={() => void redeem()}
        >
          {busy ? 'Opening…' : 'Continue'}
        </button>
        <a
          href="#signin"
          className="lm-si-link lm-si-back"
          onClick={(e) => {
            e.preventDefault();
            onBack();
          }}
        >
          Back to sign in
        </a>
      </div>
    </section>
  );
}
